import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { Review, ReviewIssueStatus } from 'components/supplier_show/prop_shapes/review'

export interface TrackedIssue {
  issue_id: number
  status: ReviewIssueStatus
}

interface ReviewIssueContextValue {
  issueFor: (reviewId: number, fallback: TrackedIssue | null) => TrackedIssue | null
  setIssueFor: (reviewId: number, issue: TrackedIssue | null) => void
}

export const ReviewIssueContext = createContext<ReviewIssueContextValue | null>(null)

export const ReviewIssueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [issues, setIssues] = useState<Map<number, TrackedIssue | null>>(() => new Map())

  const setIssueFor = useCallback((reviewId: number, issue: TrackedIssue | null): void => {
    setIssues(current => new Map(current).set(reviewId, issue))
  }, [])

  const value = useMemo(() => ({
    issueFor: (reviewId: number, fallback: TrackedIssue | null) =>
      issues.has(reviewId) ? (issues.get(reviewId) ?? null) : fallback,
    setIssueFor
  }), [issues, setIssueFor])

  return <ReviewIssueContext.Provider value={value}>{children}</ReviewIssueContext.Provider>
}

export const useTrackedIssue = (
  review: Review
): [TrackedIssue | null, (issue: TrackedIssue | null) => void] => {
  const sharedIssues = useContext(ReviewIssueContext)
  const initialIssue = review.issue_tracking?.issue ?? null
  const [localIssue, setLocalIssue] = useState(initialIssue)

  const setSharedIssue = useCallback((issue: TrackedIssue | null): void => {
    sharedIssues?.setIssueFor(review.id, issue)
  }, [sharedIssues, review.id])

  if (sharedIssues == null) return [localIssue, setLocalIssue]

  return [sharedIssues.issueFor(review.id, initialIssue), setSharedIssue]
}
